import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import {
    GenericApiServiceFactory,
    GenericBoundedApiService
} from '@lsq-frontend/angular-shared/api-service';
import { ApiRoutes } from '@lsq-frontend/angular-shared/app-constants';
import {
    AnalyticsEvent,
    AnalyticsService,
    AnalyticsValue
} from '@lsq-frontend/angular-shared/features/analytics';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import {
    CreateOrUpdateUserStateRequest,
    GetUserStateRequest,
    GetUserStateResponse,
    UserStateStatus
} from '@lsq-frontend/remote-services/enterprise/common';
import { GuidedTour, GuidedTourService } from 'ngx-guided-tour';
import { filter, first } from 'rxjs/operators';
import { HideOpenElementsService } from '../services/hide-open-elements/hide-open-elements.service';

@Injectable()
export class TourHelperService {
    private _getUserStateService: GenericBoundedApiService;
    private _createorUpdateUserStateService: GenericBoundedApiService;

    constructor(
        private _genericApiServiceFactory: GenericApiServiceFactory,
        private _analyticsService: AnalyticsService,
        private _guidedTourService: GuidedTourService,
        private _userService: UserService,
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        public hideOpenElementsService: HideOpenElementsService
    ) {
        this.hideOpenElementsService.hideOpenElementsStream.subscribe(() => {
            this._guidedTourService.resetTour();
        });
        this._getUserStateService = this._genericApiServiceFactory.createBounded(
            `/${ApiRoutes.Api}/${ApiRoutes.GetUserState}`
        );
        this._createorUpdateUserStateService = this._genericApiServiceFactory.createBounded(
            `/${ApiRoutes.Api}/${ApiRoutes.CreateOrUpdateUserState}`
        );

        // Close tours on navigation.
        this._router.events
            .pipe(filter((e) => e instanceof NavigationStart))
            .subscribe((event: any) => {
                this._guidedTourService.resetTour();
            });
    }

    public attachEndEvents(guidedTour: GuidedTour, analyticsName: string): GuidedTour {
        guidedTour = {
            ...guidedTour,
            skipCallback: (stepSkippedOn: number) => {
                this._analyticsService.triggerEventWithPayload(AnalyticsEvent.Tour, {
                    tourType: analyticsName,
                    action: AnalyticsValue.TourSkipped,
                    step: stepSkippedOn + 1
                });
                this._createorUpdateUserStateService.execute(
                    new CreateOrUpdateUserStateRequest({
                        userStateType: guidedTour.tourId,
                        userStateStatus: UserStateStatus.Skipped
                    })
                );
            },
            completeCallback: () => {
                this._analyticsService.triggerEventWithPayload(AnalyticsEvent.Tour, {
                    tourType: analyticsName,
                    action: AnalyticsValue.TourCompleted
                });
                this._createorUpdateUserStateService.execute(
                    new CreateOrUpdateUserStateRequest({
                        userStateType: guidedTour.tourId,
                        userStateStatus: UserStateStatus.Done
                    })
                );
            }
        };

        return guidedTour;
    }

    public async startTour(guidedTour: GuidedTour): Promise<void> {
        let onSamePage = true;

        // We skip starting the tour if the page has been changed since the tour was told to start.
        const navigationSubscription = this._router.events
            .pipe(filter((event) => event instanceof NavigationStart))
            .subscribe(() => {
                onSamePage = false;
            });

        const response = (await this._getUserStateService.executeOnce(
            new GetUserStateRequest({
                userStateType: guidedTour.tourId
            })
        )) as GetUserStateResponse;

        if (onSamePage) {
            const queryParamMap = this._activatedRoute.snapshot.queryParamMap;
            const showTourParam = queryParamMap.get('showTour');

            const userStateStartTour =
                !response.userState ||
                !response.userState.userStateStatus ||
                response.userState.userStateStatus === UserStateStatus.NotStarted;

            const user = await this._userService.user$.pipe(first()).toPromise();
            if ((userStateStartTour && !user.isImpersonating) || showTourParam) {
                this._guidedTourService.startTour(guidedTour);
            }
        }
        navigationSubscription.unsubscribe();
    }
}
